import type { FC } from 'react'
import { useState } from 'react'

import { type UploadFile } from '@procurify/ui'
import { useSelector } from 'react-redux'

import { ATTACHMENTS_URL } from '@webapp/api/AttachmentsAPI'
import {
  LegacyImportAPI,
  type LegacyImporterSubmitPayload,
} from '@webapp/api/ImportAPI'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global/selectors'
import { ImprovedImportTypes } from '@webapp/models/enums'
import { type Enum } from '@webapp/utils'
import {
  BaseImporter,
  type BaseImporterStage,
  BASE_IMPORTER_STAGES,
} from './BaseImporter'
import {
  type CommonImporterError,
  CommonImporterErrorStage,
} from './components/ImporterErrorStage/CommonImporterErrorStage'
import { LegacyImporterFileErrorStage } from './components/ImporterErrorStage/LegacyImporterFileErrorStage'
import { UNKNOWN_NUM_CSV_COLUMNS } from './components/ImporterUploadStage/ImporterUploadStage'
import { ProLicenseExceededStage } from './components/ProLicenseExceededStage'
import { IMPORTER_ERROR_CODES } from './constants'
import { type IImporterProps } from './Importer'
import { type ImporterColumnMap, type ImporterSampleData } from './types'
import { type ErrorRow, formatErrorsByErrorCode } from './utils'

export const LEGACY_IMPORTER_STAGES = {
  DATA_ERROR: -1,
  FILE_ERROR: -2,
  IMPROVED_DATA_ERROR: -3,
  PRO_LICENSE_ERROR: -4,
} as const
export type LegacyImporterStage = Enum<typeof LEGACY_IMPORTER_STAGES>

const STAGES = { ...BASE_IMPORTER_STAGES, ...LEGACY_IMPORTER_STAGES }

export interface IImporterModifiedRowCounts {
  CREATE: number
  DEACTIVATE: number
  IGNORE: number
  UPDATE: number
}

const DEFAULT_COUNTS = {
  CREATE: 0,
  DEACTIVATE: 0,
  IGNORE: 0,
  UPDATE: 0,
}

export const LegacyImporter: FC<IImporterProps> = ({
  type,
  title,
  requiredFields,
  optionalFields,
  customfields,
  forceOptionalCustomfields,
  fieldNameMapper,
  fieldMatcher,
  criteria,
  templateEndpoint,
  importEndpoint,
  onDismiss,
}) => {
  const [stage, setStage] = useState<LegacyImporterStage | BaseImporterStage>(
    STAGES.UPLOAD
  )
  const [uploadedCSV, setUploadedCSV] = useState<string>('')
  const [fallbackUploadedCSV, setFallbackUploadedCSV] = useState<string>('')
  const [sampleData, setSampleData] = useState<ImporterSampleData>(null)
  const [modifiedRowCounts, setModifiedRowCounts] =
    useState<IImporterModifiedRowCounts>(DEFAULT_COUNTS)
  const [errors, setErrors] = useState<CommonImporterError[][]>([[]])
  const [failedRowCount, setFailedRowCount] = useState<number>(0)
  const [fileErrorCode, setFileErrorCode] = useState<string>('')
  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
  const [numColsInCSV, setNumColsInCSV] = useState<number>(
    requiredFields.length
  )
  const { enable_seat_limit_enforcement: enableSeatLimitEnforcement } =
    useSelector(makeSelectFeatureSwitches())
  const isImprovedImporter = !!ImprovedImportTypes[type]

  const goToStage = (newStage: LegacyImporterStage | BaseImporterStage) => {
    setStage(newStage)
  }

  const onFileAdded = () => {
    setIsSubmitButtonLoading(true)
  }

  const onCSVUpload = async (file: UploadFile) => {
    setIsSubmitButtonLoading(false)
    const uuid = file.rawResponse?.data?.uuid

    if (isImprovedImporter) {
      setUploadedCSV(uuid)

      if (uuid) {
        LegacyImportAPI.getSampleData(type, uuid)
          .then((res) => {
            setSampleData({ rows: res.data })
          })
          .catch((res) => {
            onImportFileError(res?.code)

            return null
          })
      }
    } else {
      const res = file.rawResponse

      if (res?.data?.csv_cache_key) {
        setUploadedCSV(res.data.csv_cache_key)
        setSampleData(res.data?.csv_data)
      } else if (!res?.success && !file.error) {
        onImportFileError(res?.code)
      }
    }
  }

  const onCSVRemove = () => {
    setUploadedCSV('')
    setFallbackUploadedCSV('')

    if (numColsInCSV < 0) {
      setNumColsInCSV(requiredFields.length)
    }
  }

  const onSubmitCSVUpload = () => {
    goToStage(STAGES.PREVIEW)
  }

  const onPreviewConfirm = async ({
    mappedColumns,
    ignoreFirstRow,
  }: {
    mappedColumns: ImporterColumnMap
    ignoreFirstRow: boolean
  }): Promise<void> => {
    setIsSubmitButtonLoading(true)

    const submitPayload: LegacyImporterSubmitPayload = {
      column_mapping: mappedColumns,
      [isImprovedImporter ? 'csv_file_uuid' : 'csv_cache_key']:
        uploadedCSV || fallbackUploadedCSV,
      ignore_first_row: ignoreFirstRow,
    }

    try {
      const { data } = await (isImprovedImporter
        ? LegacyImportAPI.submitImproved(type, submitPayload)
        : LegacyImportAPI.submit(type, submitPayload))
      updateImportRowCounts(data)
      setIsSubmitButtonLoading(false)
      onImportSuccess()
    } catch (err) {
      let errorRows: ErrorRow[]
      let errorCode: string

      if (isImprovedImporter) {
        errorRows = err?.data?.data
        errorCode =
          err.status === 400 ? IMPORTER_ERROR_CODES.DATA_ERROR : undefined
      } else {
        errorRows = err.data?.data?.data
        errorCode = err?.data?.errors?.v1error?.code

        if (!errorCode) {
          const codeError = err?.data?.errors_with_codes?.[0]
          if (codeError?.code) {
            errorCode = codeError.code
          }
        }
      }
      setIsSubmitButtonLoading(false)
      if (
        enableSeatLimitEnforcement &&
        errorCode === IMPORTER_ERROR_CODES.PRO_SEAT_LIMIT_EXCEEDED
      ) {
        goToStage(STAGES.PRO_LICENSE_ERROR)
      } else if (
        errorCode === IMPORTER_ERROR_CODES.DATA_ERROR &&
        Array.isArray(errorRows) &&
        errorRows.length > 0
      ) {
        onImportError(errorRows)
      } else {
        onImportFileError(errorCode)
      }
    }
  }

  const onPreviewCancel = () => {
    onCSVRemove()
    goToStage(STAGES.UPLOAD)
  }

  const onImportSuccess = () => {
    goToStage(STAGES.COMPLETE)
  }

  const updateImportRowCounts = (importResponse) => {
    if ('CREATE' in importResponse) {
      const { CREATE, DEACTIVATE, IGNORE, UPDATE } = importResponse
      setModifiedRowCounts({
        CREATE,
        DEACTIVATE,
        IGNORE,
        UPDATE,
      })
    } else {
      setModifiedRowCounts({
        CREATE: importResponse.length,
        DEACTIVATE: 0,
        IGNORE: 0,
        UPDATE: 0,
      })
    }
  }

  const onImportError = (errorRows) => {
    if (uploadedCSV) setFallbackUploadedCSV(uploadedCSV)
    setUploadedCSV('')

    const formattedErrors = formatErrorsByErrorCode(errorRows, fieldNameMapper)
    setErrors(formattedErrors)
    setFailedRowCount(
      formattedErrors.reduce((totalLength, errorGroup) => {
        return totalLength + errorGroup.length
      }, 0)
    )

    goToStage(STAGES.DATA_ERROR)
  }

  const onImportFileError = (code) => {
    setUploadedCSV('')
    setFileErrorCode(code)

    if (code === IMPORTER_ERROR_CODES.INCOMPLETE && stage === STAGES.UPLOAD) {
      setNumColsInCSV(UNKNOWN_NUM_CSV_COLUMNS)
    } else {
      goToStage(STAGES.FILE_ERROR)
    }
  }

  return (
    <BaseImporter<LegacyImporterStage>
      onPreviewConfirm={onPreviewConfirm}
      onPreviewCancel={onPreviewCancel}
      showUploadAssets={false}
      stage={stage}
      sampleData={sampleData}
      modifiedRowCounts={modifiedRowCounts}
      noFileUploaded={!uploadedCSV}
      onFileAdded={onFileAdded}
      onCSVUpload={onCSVUpload}
      onCSVRemove={onCSVRemove}
      onSubmitCSVUpload={onSubmitCSVUpload}
      title={title}
      requiredFields={requiredFields}
      optionalFields={optionalFields}
      customfields={customfields}
      forceOptionalCustomfields={forceOptionalCustomfields}
      fieldNameMapper={fieldNameMapper}
      fieldMatcher={fieldMatcher}
      criteria={criteria}
      templateEndpoint={templateEndpoint}
      importEndpoint={isImprovedImporter ? ATTACHMENTS_URL : importEndpoint}
      onDismiss={onDismiss}
      isButtonLoading={isSubmitButtonLoading}
      numColumnsInCSV={numColsInCSV}
      customStages={{
        [STAGES.FILE_ERROR]: (
          <LegacyImporterFileErrorStage
            errorType={fileErrorCode}
            onGoToUpload={() => goToStage(STAGES.UPLOAD)}
            onDismiss={onDismiss}
            numRequiredFields={requiredFields.length}
            key={STAGES.FILE_ERROR}
          />
        ),
        [STAGES.PRO_LICENSE_ERROR]: (
          <ProLicenseExceededStage
            onDismiss={onDismiss}
            onUploadNewFile={() => {
              onCSVRemove()
              goToStage(STAGES.UPLOAD)
            }}
            key={STAGES.PRO_LICENSE_ERROR}
          />
        ),
        [STAGES.DATA_ERROR]: (
          <CommonImporterErrorStage
            errors={errors}
            failedRowCount={failedRowCount}
            title={title}
            onPreviousStep={() => goToStage(STAGES.PREVIEW)}
            onFirstStep={() => goToStage(STAGES.UPLOAD)}
            onDismiss={onDismiss}
            key={STAGES.IMPROVED_DATA_ERROR}
          />
        ),
      }}
    />
  )
}
